import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { Sheet, Dashboard } from '@tableau/embedding-api';
import {
  FilterUpdateType,
  TableauEventType,
  TableauViz,
  Toolbar,
  CrosstabFileFormat,
} from '@tableau/embedding-api';
import { Snackbar, Alert, IconButton } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import Loader from '../Loader';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import MainLayout from '../Layouts/Mainlayout';
import ExportMenu from '../Dashboard/ExportMenu';
import CommonModal from '../Dashboard/ExportReportModal';

interface TableauVizExtended {
  workbook: {
    activeSheet: Sheet & {
      worksheets: {
        applyFilterAsync: (
          fieldName: string,
          values: string[],
          updateType: FilterUpdateType,
        ) => Promise<void>;
        name: string;
      }[];
      name: string;
      sheetType: string;
    };
    name: string;
  };
  exportPDFAsync: () => Promise<void>;
  exportImageAsync: () => Promise<string>;
  exportCrosstabAsync: (
    sheetName: string,
    format: CrosstabFileFormat,
  ) => Promise<void>;
  addEventListener: (event: TableauEventType, callback: () => void) => void;
}

const DetailedSavedReport: React.FC = () => {
  const vizRef = useRef<HTMLDivElement | null>(null);
  const vizInstance = useRef<TableauVizExtended | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [worksheetNames, setWorksheetNames] = useState<string[]>([]);
  const [showWorksheetModal, setShowWorksheetModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const { filterData, reportName } = location.state as {
    filterData: string;
    reportName: string;
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const parsedFilterData: Record<string, string[]> = JSON.parse(filterData);

  const dashboardUrl = process.env.REACT_APP_DASHBOARD_URL ?? '';
  const { tableauToken } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (tableauToken != null) {
      setupTableauViz();
    }
  }, [tableauToken]);

  const setupTableauViz = (): void => {
    setIsLoading(true);
    if (vizRef.current && tableauToken != null) {
      const viz = new TableauViz();
      viz.src = dashboardUrl;
      viz.toolbar = Toolbar.Hidden;
      viz.hideTabs = true;
      viz.token = tableauToken;
      viz.addEventListener(TableauEventType.FirstInteractive, () => {
        void handleFirstInteractive(viz as unknown as TableauVizExtended);
      });
      vizRef.current.innerHTML = '';
      vizRef.current.appendChild(viz);
      vizInstance.current = viz as unknown as TableauVizExtended;
    }
  };

  const handleFirstInteractive = async (
    viz: TableauVizExtended,
  ): Promise<void> => {
    setIsLoading(true);
    const { workbook } = viz;
    const { activeSheet } = workbook;
    const sheets = activeSheet.worksheets;

    try {
      const filterPromises = Object.entries(parsedFilterData).map(
        ([filterName, values]) =>
          sheets[0].applyFilterAsync(
            filterName,
            values as string[],
            FilterUpdateType.Replace,
          ),
      );

      await Promise.all(filterPromises);
      setSnackbarMessage('Dashboard loaded successfully');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error applying filters:', error);
      setSnackbarMessage('Error applying filters');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ): void => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleExportClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
    setShowExportMenu(!showExportMenu);
  };

  const handleExportClose = (): void => {
    setShowExportMenu(false);
    setAnchorEl(null);
  };

  const handleExport = (exportType: string): void => {
    if (vizInstance.current) {
      const viz = vizInstance.current;

      switch (exportType) {
        case 'Image':
          void exportAsImage(viz);
          break;
        case 'PDF':
          void exportAsPDF(viz);
          break;
        case 'CrossTab':
          void exportAsCrossTab(viz);
          break;
        default:
          console.log(`Export as ${exportType} not implemented yet`);
      }
    }
    setShowExportMenu(false);
  };

  const exportAsImage = async (viz: TableauVizExtended): Promise<void> => {
    try {
      const imageData = await viz.exportImageAsync();
      const link = document.createElement('a');
      link.href = imageData;
      link.download = 'dashboard_export.png';
      link.click();
      setSnackbarMessage('Image export successful!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error exporting image:', error);
      setSnackbarMessage('Error exporting image!');
      setSnackbarOpen(true);
    }
  };

  const exportAsPDF = async (viz: TableauVizExtended): Promise<void> => {
    try {
      await viz.exportPDFAsync();
      setSnackbarMessage('PDF export successful!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setSnackbarMessage('Error exporting PDF!');
      setSnackbarOpen(true);
    }
  };

  const exportAsCrossTab = async (viz: TableauVizExtended): Promise<void> => {
    try {
      const { activeSheet } = viz.workbook;

      if (activeSheet.sheetType === 'dashboard') {
        const dashboard = activeSheet as unknown as Dashboard;
        const worksheets = dashboard.worksheets;
        const names = worksheets.map((worksheet) => worksheet.name);
        setWorksheetNames(names);
        setShowWorksheetModal(true);
      } else {
        await viz.exportCrosstabAsync(
          activeSheet.name,
          CrosstabFileFormat.Excel,
        );
        setSnackbarMessage('CrossTab export successful!');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error exporting crosstab data:', error);
      setSnackbarMessage('Error exporting crosstab data!');
      setSnackbarOpen(true);
    }
  };

  const handleWorksheetExport = async (
    selectedSheets: string[],
  ): Promise<void> => {
    if (vizInstance.current) {
      const viz = vizInstance.current;
      try {
        for (const sheet of selectedSheets) {
          await viz.exportCrosstabAsync(sheet, CrosstabFileFormat.Excel);
          console.log(`Successfully initiated export for worksheet: ${sheet}`);
        }
        setSnackbarMessage('Worksheet exported successfully');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error exporting worksheets:', error);
        setSnackbarMessage('Error exporting worksheets!');
        setSnackbarOpen(true);
      }
    }
    setShowWorksheetModal(false);
  };

  return (
    <div className="flex flex-row justify-start">
      <MainLayout>
        <div className="relative">
          <div className="flex justify-between items-center mb-2 p-2 rounded">
            <h1 className="text-2xl font-bold">
              {reportName !== '' ? reportName : ''}
            </h1>
            {!isLoading && (
              <IconButton
                onClick={handleExportClick}
                style={{ color: 'white', backgroundColor: '#ED4B52' }}
                className="rounded-md hover:bg-red-500 focus:outline-none"
              >
                <GetAppIcon />
              </IconButton>
            )}
            {showExportMenu && (
              <ExportMenu
                onExport={handleExport}
                onClose={handleExportClose}
                anchorEl={anchorEl}
              />
            )}
          </div>

          <div
            className="mb-[30px] bg-white rounded-lg p-[20px] overflow-auto relative"
            style={{ minHeight: '500px' }}
          >
            {isLoading && <Loader />}
            <div ref={vizRef} className="mt-4" />
          </div>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity="success"
              sx={{ width: '100%' }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
          <CommonModal
            open={showWorksheetModal}
            onClose={() => setShowWorksheetModal(false)}
            worksheets={worksheetNames}
            title="Select sheets from this dashboard"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onExport={handleWorksheetExport}
          />
        </div>
      </MainLayout>
    </div>
  );
};

export default DetailedSavedReport;
